import { useState } from "react";
import type { StepDetailProps } from "./index";
import { STEP_OUTPUT_SCHEMAS, zodIssuesToErrors } from "./schemas";
import { StepActions } from "./StepActions";

interface ChecklistItem {
  id: string;
  label: string;
  done?: boolean;
}

function readChecklist(inputs: Record<string, unknown>): ChecklistItem[] {
  const raw = inputs["checklist"];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is ChecklistItem => {
      if (!c || typeof c !== "object") return false;
      const r = c as Record<string, unknown>;
      return typeof r["id"] === "string" && typeof r["label"] === "string";
    })
    .map((c) => ({ id: c.id, label: c.label, done: Boolean(c.done) }));
}

export function MicroTaskDetail({
  step,
  play,
  workspaceId,
  currentUserId,
  persona,
}: StepDetailProps) {
  const items = readChecklist(step.inputs);
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(items.filter((i) => i.done).map((i) => i.id)),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const buildOutput = (): Record<string, unknown> | null => {
    const payload = { completed_items: Array.from(checked) };
    const parsed = STEP_OUTPUT_SCHEMAS["micro_task"].safeParse(payload);
    if (!parsed.success) {
      const map: Record<string, string> = {};
      for (const e of zodIssuesToErrors(parsed.error)) map[e.path] = e.message;
      setErrors(map);
      return null;
    }
    setErrors({});
    return parsed.data as Record<string, unknown>;
  };

  return (
    <div className="ho-detail ho-task" data-testid="step-detail-micro-task">
      <div className="ho-eb">MICRO TASK · STEP {step.ord + 1}</div>
      <h2 className="ho-h2">{step.title}</h2>
      <div className="ho-meta-row">
        <span style={{ color: "var(--text-3)" }}>
          {items.length} checklist item{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="ho-task-b" data-testid="micro-task-checklist">
        {items.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>
            No checklist items.
          </div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`task-item-${it.id}`}
                style={{ padding: "6px 0", display: "flex", gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={checked.has(it.id)}
                  onChange={() => toggle(it.id)}
                  disabled={step.state !== "running"}
                />
                <span>{it.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {errors["completed_items"] ? (
        <span
          style={{ color: "var(--danger)", fontSize: 12 }}
          data-testid="task-error-items"
        >
          {errors["completed_items"]}
        </span>
      ) : null}

      <StepActions
        step={step}
        workspaceId={workspaceId}
        playId={play.id}
        currentUserId={currentUserId}
        persona={persona}
        buildOutput={buildOutput}
      />
    </div>
  );
}
